package structs.promo

import kotlinx.serialization.Serializable
import structs.accelbyte.promo.PromoOutcomeType
import structs.account.AccountPlan

/**
 * Single row in the cloud-save-stored promo-code registry.
 *
 * Lives inside [PromoCodeRegistryEntry] under the `codes[]` array of the
 * admin game record keyed `promo-codes` in the namespace
 * `echoofmaridia-autogenesis`. One JSON document = the operator's
 * complete promo inventory, written by the KVision admin console
 * (or any other client with `CLOUDSAVE:RECORD` admin perms) and read
 * by `PromoCodeRegistryStorage` during player-side redemption.
 *
 * @property code The literal code the player types. Case-insensitive
 *   match on redemption (we uppercase before lookup).
 * @property status `ACTIVE` or `REDEEMED`. Single-use: flips on first
 *   successful redemption. No batch or quantity > 1 — single row per
 *   code on purpose.
 * @property plan Subscription tier this code grants on redemption.
 *   Mirrors [AccountPlan] for the SubscriptionAddOn the applier
 *   produces. Plan is the wire-shape contract the effect applier
 *   reads; changing it changes the reward.
 * @property extraSubscriptionDays Calendar days the redeemed add-on
 *   lasts. Drives `expiresMillis = nowMs + days * 86_400_000L`.
 * @property batchName Operator-assigned label for grouping in the
 *   admin console UI. Not used at redemption time.
 * @property itemName Display label rendered in the player overlay's
 *   success card. Optional — defaults to the code value if blank.
 * @property quantity Unused on subscription codes today; reserved
 *   for future credit-grant entries where this maps 1:1 to
 *   `BillingStatus.credits += quantity` when
 *   `BillingStatus.promoCreditsEnabled` is on.
 * @property redeemedBy Optional. The AccelByte userId that consumed
 *   this code. Set on first successful redemption; `null` while
 *   `status == ACTIVE`. Audit trail, not a duplicate guard.
 * @property redeemedAtMillis Epoch millis of the redemption. `0L`
 *   while unredeemed.
 * @property createdAtMillis Epoch millis the operator defined the
 *   code. Audit only.
 */
@Serializable
data class PromoCodeRegistryEntry(
    val code: String,
    val status: String = STATUS_ACTIVE,
    /** Explicit reward family. Legacy rows default to subscriptions. */
    val outcomeType: PromoOutcomeType = PromoOutcomeType.SUBSCRIPTION_MONTHS,
    /** Type-specific amount: months for subscriptions, credits for grants. */
    val rewardAmount: Int = 0,
    val plan: AccountPlan = AccountPlan.REPUBLIC,
    val extraSubscriptionDays: Int = 30,
    val batchName: String = "",
    val itemName: String = "",
    val quantity: Int = 1,
    val redeemedBy: String? = null,
    val redeemedAtMillis: Long = 0L,
    val createdAtMillis: Long = 0L,
    val redemptionOperationId: String? = null,
    val claimingUserId: String? = null,
    val claimTimestampMillis: Long = 0L,
)
{
    companion object
    {
        const val STATUS_ACTIVE: String = "ACTIVE"
        const val STATUS_REDEEMING: String = "REDEEMING"
        const val STATUS_REDEEMED: String = "REDEEMED"
    }

    /** Resolves the value for both new and legacy registry rows. */
    fun effectiveRewardAmount(): Int = when (outcomeType)
    {
        PromoOutcomeType.SUBSCRIPTION_MONTHS ->
            if (rewardAmount > 0) rewardAmount else (extraSubscriptionDays / 30).coerceAtLeast(1)
        PromoOutcomeType.CREDITS_GRANT ->
            if (rewardAmount > 0) rewardAmount else quantity
    }
}
